import { getTecgymAdminAuthenticateURL, getTecgymAdminRolesURL } from "./apiUrl";
import { buildAuthHeaders } from "./authHeaders";
import {
  APP_ACTIVITY_PRIVILEGES,
  CLIENT_PRIVILEGES,
  CONTACT_US_PRIVILEGES,
  DATABASE_SHEETS_PRIVILEGES,
  EMPLOYEE_MANAGEMENT_PRIVILEGES,
  HOME_PRIVILEGES,
  LOGIN_LOGS_PRIVILEGES,
  MOBILE_APP_PRIVILEGES,
  QUERIES_PRIVILEGES,
  PUBLISHED_PRIVILEGES,
  TECFOLK_ACTIVITY_PRIVILEGES,
  canViewAppActivityFromPrivileges,
  canViewQueriesFromPrivileges,
  extractPrivilegesFromAuthPayload,
  extractPrivilegesFromRole,
  extractRolesList,
  getAllowedBranchTabsFromPrivileges,
  getRoleNameFromObject
} from "./privileges";

const ADMIN_AUTH_STORAGE_KEY = "tecGymAdminAuthenticated";
const ADMIN_USER_STORAGE_KEY = "tecGymAdminUser";
const ADMIN_TOKEN_STORAGE_KEY = "tecGymAdminToken";
const ADMIN_API_TOKEN_STORAGE_KEY = "tecGymAdminApiToken";
const ADMIN_PROFILE_PHOTOS_STORAGE_KEY = "tecGymAdminProfilePhotos";
export const ADMIN_PROFILE_PHOTO_UPDATED_EVENT = "tecgym:profile-picture-updated";
const ADMIN_PASSWORD_STORAGE_KEY = "tecGymAdminPassword";
const ADMIN_SESSION_STARTED_KEY = "tecGymAdminSessionStartedAt";
const LEGACY_SYSTEM_ACCOUNT_STORAGE_KEY = "tecGymSystemAccount";

/** Absolute session lifetime — after this, user must re-login. */
export const ADMIN_ROLE_SESSION_TIMEOUT_MS = 60 * 60 * 1000; // 60 minutes for admin
export const OTHER_ROLE_SESSION_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes for other roles
export const ADMIN_SESSION_TIMEOUT_MS = OTHER_ROLE_SESSION_TIMEOUT_MS;
export const ADMIN_SESSION_EXPIRED_EVENT = "tecgym:admin-session-expired";

export function getAdminSessionTimeoutMs(user = getAdminUser()) {
  return isLoggedInAdmin(user) ? ADMIN_ROLE_SESSION_TIMEOUT_MS : OTHER_ROLE_SESSION_TIMEOUT_MS;
}

const DEV_ADMIN_LOGIN = {
  loginId: "admin@tecfolk.com",
  password: "1122"
};

const DEV_ADMIN_PRIVILEGES = [
  ...HOME_PRIVILEGES,
  ...CLIENT_PRIVILEGES,
  ...EMPLOYEE_MANAGEMENT_PRIVILEGES,
  ...APP_ACTIVITY_PRIVILEGES,
  ...QUERIES_PRIVILEGES,
  ...TECFOLK_ACTIVITY_PRIVILEGES,
  ...PUBLISHED_PRIVILEGES,
  ...CONTACT_US_PRIVILEGES,
  ...LOGIN_LOGS_PRIVILEGES,
  ...DATABASE_SHEETS_PRIVILEGES,
  ...MOBILE_APP_PRIVILEGES,
  "Go to Details - Dashboard",
  "Go to Details - Onboarding Objects",
  "Go to Details - Onboarding Status",
  "Go to Details - Branch Management",
  "Go to Details - User Management",
  "Go to Details - Clients querries",
  "Go to Details - AnyDesk",
  "Go to Details - Zone Management",
  "Go to Details - Packages",
  "Go to Details - Analytics",
  "Go to Details - Update License",
  "Go to Details - Update License - License",
  "Go to Details - Update License - License Update Request",
  "Go to Details - Update License - Request Status",
  "Go to Details - Desktop Credential",
  "Go to Details - Activity Log",
  "Go to Details - Sync All"
];

function isDevAdminLogin(loginId, password) {
  return loginId === DEV_ADMIN_LOGIN.loginId && password === DEV_ADMIN_LOGIN.password;
}

function buildDevAdminAuthResult(loginId, password) {
  return {
    ok: true,
    user: {
      _id: "dev-admin-tecfolk",
      name: "Admin",
      loginId,
      email: loginId,
      designation: "admin",
      role: { name: "admin" }
    },
    role: {
      name: "admin",
      privileges: DEV_ADMIN_PRIVILEGES
    },
    privileges: DEV_ADMIN_PRIVILEGES,
    token: "tecgym-admin-dev-tecfolk",
    loginId,
    password
  };
}

const DEV_AUTH_TOKEN = "tecgym-admin-dev-tecfolk";

function isDevAuthToken(token) {
  return token === DEV_AUTH_TOKEN;
}

export function getAdminDesignation(user = getAdminUser()) {
  if (!user) {
    return "";
  }

  if (typeof user.role === "object" && user.role) {
    return user.designation || getRoleNameFromObject(user.role) || "";
  }

  return user.designation || user.role || "";
}

function normalizeAdminRoleName(value) {
  return String(value || "").trim().toLowerCase();
}

export function isLoggedInAdmin(user = getAdminUser()) {
  if (!user) {
    return false;
  }

  const roleCandidates = [
    user.designation,
    user.role,
    typeof user.role === "object" ? getRoleNameFromObject(user.role) : "",
    user.session?.designation,
    user.session?.role,
    typeof user.session?.role === "object" ? getRoleNameFromObject(user.session.role) : ""
  ];

  return roleCandidates.some((role) => normalizeAdminRoleName(role) === "admin");
}

export function getLoggedInAdminUserId(user = getAdminUser()) {
  if (!user) {
    return "";
  }

  return String(
    user.session?._id ||
    user.session?.id ||
    user._id ||
    user.id ||
    user.userId ||
    user.userID ||
    ""
  ).trim();
}

export function getLoggedInAdminLoginId(user = getAdminUser()) {
  if (!user) {
    return "";
  }

  return String(
    user.loginId ||
    user.loginID ||
    user.email ||
    user.session?.loginId ||
    user.session?.loginID ||
    user.session?.loginMail ||
    user.session?.email ||
    ""
  ).trim().toLowerCase();
}

export function matchesLoggedInAdminUser(record, adminUser = getAdminUser()) {
  if (!record || !adminUser) {
    return false;
  }

  const loggedInUserId = getLoggedInAdminUserId(adminUser);
  const recordUserId = String(
    record._id ||
    record.id ||
    record.userId ||
    record.userID ||
    record.adminUserId ||
    record.adminUserID ||
    record.tecgymAdminUserId ||
    record.tecgymAdminUserID ||
    ""
  ).trim();

  if (loggedInUserId && recordUserId && loggedInUserId === recordUserId) {
    return true;
  }

  const loggedInLoginId = getLoggedInAdminLoginId(adminUser);
  const recordLoginId = String(
    record.loginId ||
    record.loginID ||
    record.userLoginId ||
    record.loginMail ||
    record.email ||
    record.loginEmail ||
    record.mail ||
    ""
  ).trim().toLowerCase();

  return Boolean(loggedInLoginId && recordLoginId && loggedInLoginId === recordLoginId);
}

function extractAuthUser(payload) {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const candidates = [
    payload.user,
    payload.tecgymAdminUser,
    payload.adminUser,
    payload.data?.user,
    payload.data?.tecgymAdminUser,
    payload.data?.adminUser,
    payload.data,
    payload.response?.user,
    payload.response?.tecgymAdminUser,
    payload.response?.adminUser,
    payload.response
  ];

  return candidates.find((candidate) => (
    candidate &&
    !Array.isArray(candidate) &&
    typeof candidate === "object" &&
    (
      candidate._id ||
      candidate.id ||
      candidate.name ||
      candidate.loginId ||
      candidate.loginMail ||
      candidate.email
    )
  )) || null;
}

function extractAuthRole(payload, user) {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const candidates = [
    payload.role,
    payload.tecgymAdminRole,
    payload.adminRole,
    payload.data?.role,
    payload.data?.tecgymAdminRole,
    payload.data?.adminRole,
    payload.response?.role,
    payload.response?.tecgymAdminRole,
    payload.response?.adminRole,
    user?.role
  ];

  const role = candidates.find((candidate) => candidate && typeof candidate === "object" && !Array.isArray(candidate));

  if (role) {
    return role;
  }

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return { name: candidate.trim() };
    }
  }

  return null;
}

async function resolveAuthPrivileges(data, user, role, authToken = null) {
  let privileges = extractPrivilegesFromAuthPayload({ ...data, user, role });

  if (privileges.length === 0 && role) {
    privileges = extractPrivilegesFromRole(role);
  }

  if (privileges.length === 0 && user?.role && typeof user.role === "object") {
    privileges = extractPrivilegesFromRole(user.role);
  }

  const roleName = getRoleNameFromObject(role) || getRoleNameFromObject(user?.role);

  if (privileges.length === 0 && roleName) {
    privileges = await fetchPrivilegesForRoleName(roleName, authToken);
  }

  const isAdminRole = normalizeAdminRoleName(
    roleName || user?.designation || (typeof user?.role === "string" ? user.role : "")
  ) === "admin";

  // Admin must always see everything (menus, clients, employee, queries, etc.).
  if (isAdminRole) {
    return [...new Set([...privileges, ...DEV_ADMIN_PRIVILEGES])];
  }

  return privileges;
}

async function fetchPrivilegesForRoleName(roleName, authToken = null) {
  const normalizedRoleName = String(roleName || "").trim().toLowerCase();

  if (!normalizedRoleName) {
    return [];
  }

  try {
    const response = await fetch(getTecgymAdminRolesURL(), {
      headers: buildAuthHeaders({}, false, authToken || getRequestAuthToken())
    });

    if (!response.ok) {
      return [];
    }

    const data = await response.json();
    const roles = extractRolesList(data);
    const matchedRole = roles.find((entry) => (
      getRoleNameFromObject(entry).toLowerCase() === normalizedRoleName
    ));

    return matchedRole ? extractPrivilegesFromRole(matchedRole) : [];
  } catch {
    return [];
  }
}

export async function authenticateTecgymAdmin(loginId, password) {
  const normalizedLoginId = String(loginId || "").trim().toLowerCase();
  const normalizedPassword = String(password || "").trim();

  if (!normalizedLoginId || !normalizedPassword) {
    return { ok: false, reason: "invalid_credentials" };
  }

  const serverAuthResult = await authenticateTecgymAdminWithServer(
    normalizedLoginId,
    normalizedPassword
  );

  if (serverAuthResult.ok) {
    return serverAuthResult;
  }

  if (
    isDevAdminLogin(normalizedLoginId, normalizedPassword) &&
    serverAuthResult.reason === "network_error"
  ) {
    return buildDevAdminAuthResult(normalizedLoginId, normalizedPassword);
  }

  return serverAuthResult;
}

async function authenticateTecgymAdminWithServer(loginId, password) {
  try {
    const response = await fetch(getTecgymAdminAuthenticateURL(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        loginId,
        password,
        loginPassword: password
      })
    });

    let data = null;

    try {
      data = await response.json();
    } catch {
      data = null;
    }

    console.log("TecGym admin authenticate response:", data);

    const errorMessage = extractAuthErrorMessage(data);
    const authenticated = response.ok &&
      data?.status !== false &&
      data?.authentication !== false &&
      data?.success !== false &&
      !errorMessage;

    if (!authenticated) {
      return {
        ok: false,
        reason: "invalid_credentials",
        message: errorMessage || data?.message || data?.error || "Invalid login ID or password."
      };
    }

    const user = extractAuthUser(data);
    const role = extractAuthRole(data, user);
    const token = extractAdminToken(data) ||
      extractAdminToken(data?.data || data?.response || data?.user || data) ||
      extractAdminToken(user);

    if (!token) {
      return {
        ok: false,
        reason: "invalid_credentials",
        message: "Authentication succeeded but no JWT token was returned."
      };
    }

    const privileges = await resolveAuthPrivileges(data, user, role, token);

    console.log("TecGym admin resolved privileges:", privileges);

    return {
      ok: true,
      user,
      role,
      privileges,
      token,
      loginId,
      password
    };
  } catch (error) {
    return {
      ok: false,
      reason: "network_error",
      message: error.message || "Unable to reach authentication server."
    };
  }
}

function extractAuthErrorMessage(payload) {
  if (!payload || typeof payload !== "object") {
    return "";
  }

  if (payload.success === false || payload.status === false || payload.authentication === false) {
    return String(payload.error || payload.message || "Authentication failed").trim();
  }

  const errorValues = [
    payload.error,
    payload.data?.error,
    payload.response?.error
  ]
    .filter((value) => typeof value === "string")
    .map((value) => value.trim())
    .filter(Boolean);

  return errorValues[0] || "";
}

export function saveServerAdminSession({ user, role, privileges, token, loginId, password }) {
  const resolvedLoginId = user?.loginId ||
    user?.loginMail ||
    user?.email ||
    loginId ||
    "";

  const roleName = typeof role === "string"
    ? role
    : (role?.name || role?.roleName || role?.title || "");

  saveAdminSession(
    { token, ...(user || {}) },
    {
      email: resolvedLoginId,
      loginId: resolvedLoginId,
      password,
      name: user?.name || user?.userName || user?.fullName || "",
      designation: user?.designation || roleName,
      role: roleName,
      privileges,
      authType: "server"
    }
  );

  if (token && !isDevAuthToken(token)) {
    localStorage.setItem(ADMIN_API_TOKEN_STORAGE_KEY, token);
  }
}

export const BRANCH_TABS = [
  { id: "Dashboard", label: "Dashboard" },
  { id: "OnboardingObject", label: "Onboarding Objects" },
  { id: "OnboardingStatus", label: "Onboarding Status" },
  { id: "Info", label: "Branch Management" },
  { id: "UsersInfo", label: "User Management" },
  { id: "ClientsQuerries", label: "Clients querries" },
  { id: "AnyDesk", label: "AnyDesk" },
  { id: "ZoneManagement", label: "Zone Management" },
  { id: "Packages", label: "Packages" },
  { id: "Analytics", label: "Analytics" },
  { id: "UpdateLicense", label: "Update License" },
  { id: "DesktopCredential", label: "Desktop Credential" },
  { id: "ActivityLog", label: "Activity Log" },
  { id: "SyncAll", label: "Sync All" }
];

export function getAllowedBranchTabs() {
  const privilegeTabs = getAllowedBranchTabsFromPrivileges(BRANCH_TABS);

  if (privilegeTabs) {
    return privilegeTabs;
  }

  return [];
}

export function canViewAppActivity() {
  const privilegeAccess = canViewAppActivityFromPrivileges();
  return privilegeAccess === null ? false : privilegeAccess;
}

export function canViewQueries() {
  const privilegeAccess = canViewQueriesFromPrivileges();
  return privilegeAccess === null ? false : privilegeAccess;
}

export function getAdminSessionStartedAt() {
  const raw = localStorage.getItem(ADMIN_SESSION_STARTED_KEY);
  const startedAt = Number(raw);
  return Number.isFinite(startedAt) && startedAt > 0 ? startedAt : null;
}

export function getAdminSessionRemainingMs() {
  const startedAt = getAdminSessionStartedAt();
  if (!startedAt) {
    return 0;
  }

  return Math.max(0, getAdminSessionTimeoutMs() - (Date.now() - startedAt));
}

export function isAdminSessionExpired() {
  const startedAt = getAdminSessionStartedAt();
  if (!startedAt) {
    // Legacy sessions without a start time must re-login.
    return localStorage.getItem(ADMIN_AUTH_STORAGE_KEY) === "true";
  }

  return Date.now() - startedAt >= getAdminSessionTimeoutMs();
}

function dispatchAdminSessionExpired() {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(new CustomEvent(ADMIN_SESSION_EXPIRED_EVENT));
}

/**
 * Clears the session when the role-based timeout has elapsed
 * (60 min for admin, 15 min for other roles).
 * Returns true when the user is still within a valid session.
 */
export function enforceAdminSession() {
  const hasAuthFlag = localStorage.getItem(ADMIN_AUTH_STORAGE_KEY) === "true";
  const hasToken = Boolean(getAdminToken());

  if (!hasAuthFlag || !hasToken) {
    return false;
  }

  if (isAdminSessionExpired()) {
    logoutAdmin();
    dispatchAdminSessionExpired();
    return false;
  }

  return true;
}

export function isAdminAuthenticated() {
  return enforceAdminSession();
}

export function saveAdminSession(user, profile = {}) {
  const token = extractAdminToken(user);

  if (!token) {
    throw new Error("Login response did not include an authentication token.");
  }

  localStorage.setItem(ADMIN_AUTH_STORAGE_KEY, "true");
  localStorage.setItem(ADMIN_TOKEN_STORAGE_KEY, token);
  localStorage.setItem(ADMIN_SESSION_STARTED_KEY, String(Date.now()));

  if (profile.password) {
    sessionStorage.setItem(ADMIN_PASSWORD_STORAGE_KEY, profile.password);
  }

  const { password, ...safeProfile } = profile;

  if (user || Object.keys(profile).length > 0) {
    localStorage.setItem(ADMIN_USER_STORAGE_KEY, JSON.stringify({
      ...safeProfile,
      session: user
    }));
  }
}

export function getAdminUser() {
  const storedUser = localStorage.getItem(ADMIN_USER_STORAGE_KEY);

  if (!storedUser) {
    return null;
  }

  try {
    return JSON.parse(storedUser);
  } catch {
    return null;
  }
}

export function getAdminProfilePicture(email) {
  if (!email) {
    return null;
  }

  const profilePictures = getAdminProfilePictures();
  return profilePictures[email.toLowerCase()] || null;
}

export function saveAdminProfilePicture(email, pictureDataUrl) {
  if (!email || !pictureDataUrl) {
    return;
  }

  const profilePictures = getAdminProfilePictures();
  profilePictures[email.toLowerCase()] = pictureDataUrl;

  try {
    localStorage.setItem(ADMIN_PROFILE_PHOTOS_STORAGE_KEY, JSON.stringify(profilePictures));
  } catch (error) {
    console.warn("Unable to store the profile image in this browser.", error);
    return;
  }

  dispatchProfilePhotoUpdated();
}

export function deleteAdminProfilePicture(email) {
  if (!email) {
    return;
  }

  const profilePictures = getAdminProfilePictures();
  delete profilePictures[email.toLowerCase()];
  localStorage.setItem(ADMIN_PROFILE_PHOTOS_STORAGE_KEY, JSON.stringify(profilePictures));
  dispatchProfilePhotoUpdated();
}

function dispatchProfilePhotoUpdated() {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(new CustomEvent(ADMIN_PROFILE_PHOTO_UPDATED_EVENT));
}

export function getAdminToken() {
  return localStorage.getItem(ADMIN_TOKEN_STORAGE_KEY);
}

export function getAdminApiToken() {
  return localStorage.getItem(ADMIN_API_TOKEN_STORAGE_KEY);
}

export function clearAdminApiToken() {
  localStorage.removeItem(ADMIN_API_TOKEN_STORAGE_KEY);
}

export function getRequestAuthToken() {
  const candidates = [getAdminApiToken(), getAdminToken()].filter(Boolean);
  return candidates.find((token) => !isDevAuthToken(token)) || null;
}

export async function ensureProductionApiToken() {
  if (!enforceAdminSession()) {
    return null;
  }

  const existingToken = getRequestAuthToken();
  if (existingToken && !isDevAuthToken(existingToken)) {
    return existingToken;
  }

  // Do not silently re-authenticate — expired/missing tokens require a fresh login.
  return null;
}

export function getAdminPassword() {
  return sessionStorage.getItem(ADMIN_PASSWORD_STORAGE_KEY) || "";
}

export function logoutAdmin() {
  localStorage.removeItem(ADMIN_AUTH_STORAGE_KEY);
  localStorage.removeItem(ADMIN_USER_STORAGE_KEY);
  localStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY);
  localStorage.removeItem(ADMIN_API_TOKEN_STORAGE_KEY);
  localStorage.removeItem(ADMIN_SESSION_STARTED_KEY);
  localStorage.removeItem(LEGACY_SYSTEM_ACCOUNT_STORAGE_KEY);
  sessionStorage.removeItem(ADMIN_PASSWORD_STORAGE_KEY);
}

export function expireAdminSession() {
  logoutAdmin();
  dispatchAdminSessionExpired();
}

function extractAdminToken(value) {
  if (!value) return null;

  if (typeof value === "string") {
    return value;
  }

  const tokenKeys = [
    "token",
    "jwt",
    "jwtToken",
    "accessToken",
    "access_token",
    "authToken",
    "auth_token",
    "idToken",
    "bearerToken"
  ];
  for (const key of tokenKeys) {
    if (typeof value[key] === "string" && value[key].trim()) {
      return value[key].trim();
    }
  }

  const nestedKeys = ["data", "response", "user", "admin", "result"];
  for (const key of nestedKeys) {
    const nestedToken = extractAdminToken(value[key]);
    if (nestedToken) {
      return nestedToken;
    }
  }

  return null;
}

function getAdminProfilePictures() {
  const storedProfilePictures = localStorage.getItem(ADMIN_PROFILE_PHOTOS_STORAGE_KEY);

  if (!storedProfilePictures) {
    return {};
  }

  try {
    return JSON.parse(storedProfilePictures);
  } catch {
    return {};
  }
}
